import { AppSettings } from "@/settings"

import { internalCustomerException } from "../../customer/utils/customerQueryHelpers"
import { checkPermissions, validateAccessRightsToCustomer } from "../../shared/permissions/permissions"
import { DomainObjects, Operations } from "../../shared/permissions/permissionsConfig"
import { getEmployeeReference } from "../../shared/utils/getEmployeeReference"
import { deleteAttachmentReferences } from "../../shared/utils/groqQueryOptions"
import {
  getApplicationReferences,
  getAttachmentReferences,
  getClaimReferences,
  getDefectReferences,
} from "../utils/complaintHelpers"

/**
 * Sends request to update the complaint
 */
export const mutationUpdateComplaint = {
  async updateComplaint(_parent, args, context, _info) {
    const { lang = AppSettings.defaultLocale(), id, values, customerNumber, cdn = false } = args

    const { user } = context

    await Promise.all([
      checkPermissions({
        user,
        domainObject: DomainObjects.COMPLAINTS,
        operation: Operations.UPDATE,
      }),
      validateAccessRightsToCustomer({
        user,
        customerNumber,
      }),
      internalCustomerException({ customerNumber }),
    ])

    const config = AppSettings.sanityConfigByLang(lang)
    const client = context.sanityClient
    client.config({ ...config, useCdn: cdn })

    const modifiedBy = await getEmployeeReference({
      employeeNumber: user.employeeNumber,
      sanityClient: client,
    })

    const { deletedAttachmentReferences, ...rest } = values

    const updatedDefects = (await getDefectReferences(client, rest?.defects)) || []
    const updatedApplications = (await getApplicationReferences(client, rest?.applications)) || []
    const updatedClaims = (await getClaimReferences(client, rest?.customerClaims)) || []
    const updatedAttachmentReferences = getAttachmentReferences(rest?.attachmentReferences) || []

    const patch = {
      ...rest,
      modifiedAt: new Date().toISOString(),
      defects: updatedDefects,
      applications: updatedApplications,
      customerClaims: updatedClaims,
      attachmentReferences: updatedAttachmentReferences,
      ...(modifiedBy && { modifiedBy }),
      modificationSource: "Sanity",
    }

    const jsonString = JSON.stringify(patch)
    const encoder = new TextEncoder()
    const byteArray = encoder.encode(jsonString)

    // request body can only be 4MB so if it is bigger we stop the change
    if (byteArray?.length > 4_000_000) {
      console.error("mutationUpdateComplaint(): byteArray length > 4MB")
      return null
    }

    const complaintPatch = await client
      .patch(id)
      .set({
        ...patch,
      })
      .commit()

    // delete attachment references now
    if (deletedAttachmentReferences && deletedAttachmentReferences.length > 0) {
      await deleteAttachmentReferences(deletedAttachmentReferences, client)
    }

    return {
      ...complaintPatch,
      id: complaintPatch._id,
      rev: complaintPatch._rev,
    }
  },
}
